import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Camera, Clock, Star } from 'lucide-react';
import { Button, Card, cn, TextArea, TextField } from '@tcrd/ui';
import { TripNeeds } from '../../components/TripExtras';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { pickImage } from '../../lib/media';
import type { RideDto } from '../../lib/types';
import { formatMoney } from '@tcrd/shared';

/** Mensajería: el conductor toma la foto de la entrega; el cliente la ve. */
function Delivery({ ride, role }: { ride: RideDto; role: 'customer' | 'driver' }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [to, setTo] = useState(ride.package?.recipientName ?? '');
  const [busy, setBusy] = useState(false);
  const pkg = ride.package;
  if (!pkg) return null;

  const upload = async () => {
    try {
      const blob = await pickImage('camera');
      if (!blob) return;
      setBusy(true);
      const fd = new FormData();
      fd.append('deliveredTo', to);
      fd.append('file', blob, 'entrega.jpg');
      await api(`/rides/${ride.id}/delivery`, { method: 'POST', form: fd });
      await qc.invalidateQueries({ queryKey: ['ride', ride.id] });
      toast('Entrega registrada. Avisamos al cliente.', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 flex flex-col gap-3">
      <p className="font-semibold">Entrega del paquete</p>
      {pkg.deliveryPhotoUrl ? (
        <>
          <img src={pkg.deliveryPhotoUrl} alt="Foto de la entrega" className="w-full max-h-72 rounded-2xl object-cover" />
          <p className="text-sm">Entregado a <strong>{pkg.deliveredTo ?? pkg.recipientName}</strong>.</p>
        </>
      ) : role === 'driver' && ['IN_PROGRESS', 'COMPLETED'].includes(ride.status) ? (
        <>
          <TextField label="¿Quién lo recibió?" value={to} onChange={(e) => setTo(e.target.value)} />
          <Button icon={<Camera className="size-5" />} loading={busy} onClick={() => void upload()}>Tomar foto de la entrega</Button>
        </>
      ) : (
        <p className="text-sm text-[var(--text-muted)]">Cuando se entregue verás aquí la foto de la entrega.</p>
      )}
    </Card>
  );
}

/** El conductor califica al cliente al terminar (solo lo ven los conductores). */
function RatePassenger({ ride }: { ride: RideDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  if (ride.status !== 'COMPLETED' || !ride.customer) return null;
  if (ride.passengerReview) {
    return (
      <Card className="p-4 text-sm flex items-center gap-2">
        <Star className="size-4 fill-current text-amber-500" aria-hidden /> Calificaste a {ride.customer.firstName} con {ride.passengerReview.rating} estrella{ride.passengerReview.rating === 1 ? '' : 's'}.
      </Card>
    );
  }
  const send = async () => {
    setBusy(true);
    try {
      await api(`/rides/${ride.id}/passenger-review`, { body: { rating, comment: comment.trim() || undefined } });
      await qc.invalidateQueries({ queryKey: ['ride', ride.id] });
      toast('Gracias por calificar', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-4 flex flex-col gap-3">
      <p className="font-semibold">¿Cómo fue {ride.customer.firstName} como pasajero?</p>
      <div className="flex gap-1" role="radiogroup" aria-label="Calificación">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} estrellas`} onClick={() => setRating(n)} className="tap p-1">
            <Star className={cn('size-8', n <= rating ? 'fill-current text-amber-500' : 'text-[var(--text-muted)]')} />
          </button>
        ))}
      </div>
      <TextArea label="Comentario privado (opcional)" placeholder="Solo lo ven los conductores" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={600} />
      <Button disabled={!rating} loading={busy} onClick={() => void send()}>Enviar calificación</Button>
    </Card>
  );
}

/** Necesidades, vuelo, mensajería, cargo por espera y calificación al cliente en el detalle del viaje. */
export function RideExtrasSection({ ride, role }: { ride: RideDto; role: 'customer' | 'driver' }) {
  const hasInfo = !!ride.needs?.length || !!ride.flightNumber;
  return (
    <>
      {hasInfo && (
        <Card className="p-4">
          <TripNeeds needs={ride.needs} flightNumber={ride.flightNumber} />
        </Card>
      )}
      {ride.package && (
        <Card className="p-4">
          <TripNeeds pkg={ride.package} />
        </Card>
      )}
      <Delivery ride={ride} role={role} />
      {ride.waiting && (
        <Card className="p-4 text-sm flex items-center gap-2">
          <Clock className="size-4 text-[var(--text-muted)]" aria-hidden /> Tiempo de espera: {ride.waiting.minutes} min · cargo de {formatMoney(ride.waiting.fee)} incluido en el total.
        </Card>
      )}
      {role === 'driver' && <RatePassenger ride={ride} />}
    </>
  );
}
